import { ClipboardEvent, useCallback } from 'react';

interface UseClipboardImagePasteOptions {
  /** 貼り付けられた画像を既存の添付処理へ渡す */
  onFiles: (files: File[]) => void;
  /** true の間は貼り付けを添付に変換しない（送信中・無効化中） */
  disabled?: boolean;
}

const EXTENSION_BY_MIME: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/svg+xml': 'svg',
  'image/bmp': 'bmp',
};

const isImage = (file: File | null): file is File =>
  !!file && file.type.startsWith('image/');

/**
 * クリップボードから画像ファイルだけを取り出す。
 * Chrome 等は同じ画像を items と files の両方に載せるため、items を優先し無ければ files を使う。
 */
export const extractClipboardImages = (clipboardData: DataTransfer | null): File[] => {
  if (!clipboardData) return [];

  const fromItems = Array.from(clipboardData.items ?? [])
    .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
    .map((item) => item.getAsFile())
    .filter(isImage);
  if (fromItems.length > 0) return fromItems;

  return Array.from(clipboardData.files ?? []).filter(isImage);
};

/**
 * 貼り付け画像は "image.png" 固定になりがちなので、重複しない名前を付け直す。
 * 例: pasted-1790000000000.png / 複数枚なら pasted-1790000000000-2.png
 */
export const renamePastedImages = (files: File[], now: number = Date.now()): File[] =>
  files.map((file, index) => {
    const extension = EXTENSION_BY_MIME[file.type] ?? (file.type.split('/')[1] || 'png');
    const suffix = files.length > 1 ? `-${index + 1}` : '';
    return new File([file], `pasted-${now}${suffix}.${extension}`, {
      type: file.type,
      lastModified: file.lastModified,
    });
  });

/**
 * チャット入力へのクリップボード画像の貼り付けを、既存のファイル添付と同じ経路に載せる。
 *
 * - 画像が無い貼り付け（テキストのみ）は何もしない＝ブラウザ既定のまま
 * - 画像とテキストが同時にある場合は画像を添付し、テキストの挿入はブラウザ既定に任せる
 * - 画像だけの場合は既定動作を止める（ファイル名等が文字として入るのを防ぐ）
 */
export const useClipboardImagePaste = ({ onFiles, disabled = false }: UseClipboardImagePasteOptions) => {
  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLElement>) => {
      if (disabled) return;

      const images = extractClipboardImages(event.clipboardData);
      if (images.length === 0) return;

      if (!event.clipboardData.getData('text/plain')) {
        event.preventDefault();
      }
      onFiles(renamePastedImages(images));
    },
    [disabled, onFiles]
  );

  return { handlePaste };
};
